/**
 * ImportCreatePage — Issue #176 (CSV paste) + Issue #293 (file upload with
 * encoding detection).
 *
 * Primary flow (file upload):
 *   1. Admin selects a .csv file → bytes are read via FileReader.
 *   2. decodeCsvBytes detects encoding (UTF-8 / UTF-8 BOM / GBK) and rejects
 *      mojibake / unrecognised headers before any data leaves the browser.
 *   3. parseHrRosterCsv converts the decoded text to rows[].
 *   4. Aggregate preview (encoding, row count, unknown headers) is shown.
 *   5. Admin submits → createImport hook POSTs to the backend, which runs the
 *      canonical validator and persists pending_review.
 *   6. On success, navigate to the detail page for review / approve / commit.
 *
 * Secondary flow (paste):
 *   Same as #176; no encoding detection (browser already decoded the text).
 *
 * 红线：
 *   - 不在前端做业务校验或脱敏；所有校验在后端复算。
 *   - 不写前端假状态；mutation 必须使用后端返回值跳转。
 *   - 不在 console 打印 / 不保留原始 CSV 字节/字符串超过必要时长。
 */
import { useState, useRef } from 'react';
import { useLocation } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  parseHrRosterCsv,
  decodeCsvBytes,
  sha256HexBytes,
  sha256Hex,
  CsvEncodingError,
} from '@/lib/hrCsvParser';
import { useHrImports } from '@/hooks/useHr';

const ENCODING_LABELS: Record<string, string> = {
  'utf-8-bom': 'UTF-8（含 BOM）',
  'utf-8': 'UTF-8',
  'gbk': 'GBK / GB18030',
};

export default function ImportCreatePage() {
  const { permissions } = useAuth();
  const canWrite = hasPermission(permissions, HR_PERMISSIONS.IMPORT_WRITE);
  const [, navigate] = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [filename, setFilename] = useState('roster.csv');
  const [csv, setCsv] = useState('');
  const [fileBytes, setFileBytes] = useState<Uint8Array | null>(null);
  const [detectedEncoding, setDetectedEncoding] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [unknownHeaders, setUnknownHeaders] = useState<string[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const { error, createImport } = useHrImports();

  if (!canWrite) return <AccessDenied resource="新建员工导入" />;

  function resetPreview() {
    setPreviewCount(null);
    setUnknownHeaders([]);
    setLocalError(null);
    setFileBytes(null);
    setDetectedEncoding(null);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    resetPreview();
    setFilename(file.name);
    setCsv('');

    const reader = new FileReader();
    reader.onload = () => {
      const bytes = new Uint8Array(reader.result as ArrayBuffer);
      try {
        const decoded = decodeCsvBytes(bytes);
        const parsed = parseHrRosterCsv(decoded.text);
        setFileBytes(bytes);
        setDetectedEncoding(decoded.encoding);
        setPreviewCount(parsed.rows.length);
        setUnknownHeaders(parsed.unknownHeaders);
        if (parsed.rows.length === 0) {
          setLocalError('CSV 内容为空或缺少数据行，请确认文件格式。');
        }
      } catch (err) {
        setLocalError(
          err instanceof CsvEncodingError || err instanceof Error
            ? err.message
            : 'CSV 解析失败',
        );
      }
    };
    reader.readAsArrayBuffer(file);
  }

  function handlePreview() {
    setLocalError(null);
    try {
      const parsed = parseHrRosterCsv(csv);
      setPreviewCount(parsed.rows.length);
      setUnknownHeaders(parsed.unknownHeaders);
      if (parsed.rows.length === 0) {
        setLocalError('CSV 内容为空或缺少表头，请先粘贴一行表头 + 数据行。');
      }
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'CSV 解析失败');
    }
  }

  async function handleSubmit() {
    setLocalError(null);

    if (fileBytes !== null) {
      // File upload path: re-decode to get text, compute byte-accurate SHA-256
      let decodedText: string;
      try {
        const decoded = decodeCsvBytes(fileBytes);
        decodedText = decoded.text;
      } catch (err) {
        setLocalError(err instanceof Error ? err.message : 'CSV 解析失败');
        return;
      }
      const parsed = parseHrRosterCsv(decodedText);
      if (parsed.rows.length === 0) {
        setLocalError('CSV 内容为空或缺少数据行，请确认文件格式。');
        return;
      }
      setSubmitting(true);
      const sha256 = await sha256HexBytes(fileBytes);
      const result = await createImport({
        sourceFilename: filename,
        sourceSize: fileBytes.byteLength,
        sourceSha256: sha256,
        rows: parsed.rows,
      });
      setSubmitting(false);
      if (result) {
        navigate(`/hr/imports/${result.id}`);
      }
      return;
    }

    // Paste mode path
    const parsed = parseHrRosterCsv(csv);
    if (parsed.rows.length === 0) {
      setLocalError('CSV 内容为空或缺少表头，请先粘贴一行表头 + 数据行。');
      return;
    }
    setSubmitting(true);
    const bytes = new TextEncoder().encode(csv);
    const sha256 = await sha256Hex(csv);
    const result = await createImport({
      sourceFilename: filename,
      sourceSize: bytes.byteLength,
      sourceSha256: sha256,
      rows: parsed.rows,
    });
    setSubmitting(false);
    if (result) {
      navigate(`/hr/imports/${result.id}`);
    }
  }

  const hasInput = fileBytes !== null || csv.trim().length > 0;

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">新建员工花名册导入</h1>
        <p className="text-sm text-slate-500">
          支持 UTF-8 和 GBK/GB18030 编码的 CSV 文件（从 Excel/WPS 导出），或直接粘贴
          UTF-8 CSV 文本。支持表头：工号、姓名、性别、部门、岗位、职务、职级、入职日期、邮箱、身份证号、手机号、在职状态。
        </p>
      </header>

      <Card className="space-y-3 p-4">
        <label className="block text-sm">
          源文件名（仅用于审计记录）
          <input
            type="text"
            className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            maxLength={255}
          />
        </label>

        <div className="space-y-2">
          <p className="text-sm font-medium">选择 CSV 文件（推荐）</p>
          <p className="text-xs text-slate-500">
            自动识别 UTF-8 和 GBK/GB18030 编码；检测到乱码或无法识别的表头时拒绝导入。
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            >
              浏览文件…
            </button>
            {fileBytes !== null ? (
              <span className="text-sm text-slate-600">{filename}</span>
            ) : null}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        </div>

        <details className="text-sm">
          <summary className="cursor-pointer select-none text-slate-500 hover:text-slate-700">
            或手动粘贴 CSV 文本（仅支持 UTF-8，不检测编码）
          </summary>
          <div className="mt-2 space-y-2">
            <label className="block text-sm">
              CSV 内容（含表头）
              <textarea
                className="mt-1 h-64 w-full rounded border border-slate-300 px-3 py-2 font-mono text-xs"
                value={csv}
                onChange={(e) => {
                  setCsv(e.target.value);
                  resetPreview();
                }}
                placeholder={
                  '工号,姓名,性别,部门,岗位,入职日期,身份证号,手机号\nFIXT-E001,FIXT-甲一-001,男,研发中心,工程师,2024-01-15,IDCARD-FIXT-0001,PHONE-FIXT-0001'
                }
              />
            </label>
            <button
              type="button"
              onClick={handlePreview}
              className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            >
              本地解析预览
            </button>
          </div>
        </details>

        {previewCount !== null ? (
          <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            {detectedEncoding !== null ? (
              <span className="mr-3 text-slate-600">
                编码：
                <strong>{ENCODING_LABELS[detectedEncoding] ?? detectedEncoding}</strong>
              </span>
            ) : null}
            预览到 <strong>{previewCount}</strong> 行数据。
            {unknownHeaders.length > 0 ? (
              <span className="ml-2 text-amber-700">
                未识别的表头将被忽略：{unknownHeaders.join('、')}
              </span>
            ) : null}
          </div>
        ) : null}
        {localError ? (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {localError}
          </div>
        ) : null}
        {error ? (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error.message}
          </div>
        ) : null}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !hasInput}
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting ? '上传中…' : '上传并进入预览'}
          </button>
        </div>
      </Card>

      <Card className="space-y-2 p-4 text-sm text-slate-600">
        <p className="font-medium">⚠️ 数据安全提醒</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>禁止上传任何真实员工数据用于测试；测试请使用脱敏 fixture（前缀 FIXT-）。</li>
          <li>
            身份证 / 手机号将在上传后立即加密 +
            哈希；明文不会落库，前端展示仅保留后四位。
          </li>
          <li>本页只是 UI 解析；所有业务校验在后端复算，存在错误行的批次不允许审批通过。</li>
          <li>批次进入待审核状态后，仍可在详情页执行撤销（撤销原因必填）。</li>
        </ul>
      </Card>
    </div>
  );
}
